// -------------------------------
// ESIC EMPLOYEE MAPPING INTERFACES
// -------------------------------
export interface PendingEsicEmployee {
    employee_name?: string;
    esic_number?: string;
    esic_branch_name?: string;
    esic_enrollment_date?: string;
    is_esic_applicable?: number; // 1 | 0
    remarks?: string;
}

export interface PendingDeleteEsicEmployee {
    awaitingEmployee?: boolean;
}

export const startCreateEsicEmployeeFlow = (
    setPending: (data: PendingEsicEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeFromIntent?: string
) => {
    if (employeeFromIntent) {
        setPending({ employee_name: employeeFromIntent });

        pushAIChat("What is the ESIC number?");
        speak("What is the ESIC number?");
        return;
    }

    setPending({});
    pushAIChat("Which employee should be mapped to ESIC?");
    speak("Which employee should be mapped to ESIC?");
};

export const processPendingEsicEmployee = (
    text: string,
    pending: PendingEsicEmployee,
    setPending: (data: PendingEsicEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — EMPLOYEE NAME
    if (!pending.employee_name) {
        const name = text.trim();
        if (!name) {
            pushAIChat("Please provide a valid employee name.");
            speak("Please provide a valid employee name.");
            return;
        }

        setPending({ ...pending, employee_name: name });
        pushAIChat("What is the ESIC number?");
        speak("What is the ESIC number?");
        return;
    }

    // STEP 2 — ESIC NUMBER
    if (!pending.esic_number) {
        setPending({ ...pending, esic_number: text.trim() });
        pushAIChat("Which ESIC branch?");
        speak("Which ESIC branch?");
        return;
    }

    // STEP 3 — BRANCH
    if (!pending.esic_branch_name) {
        setPending({ ...pending, esic_branch_name: text.trim() });
        pushAIChat("What is the enrollment date? (YYYY-MM-DD)");
        speak("What is the enrollment date?");
        return;
    }

    // STEP 4 — ENROLLMENT DATE
    if (!pending.esic_enrollment_date) {
        setPending({ ...pending, esic_enrollment_date: text.trim() });
        pushAIChat("Is ESIC applicable? Yes or No?");
        speak("Is ESIC applicable?");
        return;
    }

    // STEP 5 — APPLICABLE
    if (pending.is_esic_applicable === undefined) {
        const applicable = text.toLowerCase().includes("no") ? 0 : 1;

        setPending({ ...pending, is_esic_applicable: applicable });
        pushAIChat("Any remarks?");
        speak("Any remarks?");
        return;
    }

    // STEP 6 — REMARKS (FINAL)
    if (!pending.remarks) {
        pushAIChat("Creating ESIC employee mapping...");
        speak("Creating ESIC employee mapping.");

        window.dispatchEvent(
            new CustomEvent("ai-create-esic-employee", {
                detail: {
                    ...pending,
                    remarks: text
                }
            })
        );

        setPending(null);
    }
};

export const startDeleteEsicEmployeeFlow = (
    setPendingDelete: (data: PendingDeleteEsicEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeFromIntent?: string
) => {
    if (employeeFromIntent) {
        pushAIChat(`Deleting ESIC mapping for ${employeeFromIntent}...`);
        speak(`Deleting ESIC mapping for ${employeeFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-esic-employee", {
                detail: { employee_name: employeeFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingEmployee: true });
    pushAIChat("Which employee ESIC mapping should be deleted?");
    speak("Which employee ESIC mapping should be deleted?");
};

export const processPendingDeleteEsicEmployee = (
    text: string,
    pending: PendingDeleteEsicEmployee,
    setPendingDelete: (data: PendingDeleteEsicEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingEmployee) return;

    const name = text.trim();

    if (!name) {
        pushAIChat("Please provide a valid employee name.");
        speak("Please provide a valid employee name.");
        return;
    }

    pushAIChat(`Deleting ESIC mapping for "${name}"...`);
    speak(`Deleting ESIC mapping for ${name}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-esic-employee", {
            detail: { employee_name: name }
        })
    );

    setPendingDelete(null);
};

